import React, { useState } from "react";
import styles from "./addUserModal.module.css";
import {
  TextField,
  MenuItem,
  Button,
  InputAdornment,
  Switch,
} from "@mui/material";
import { useAllProduct } from "../api-context/commonApiContext";
import config from "../service/config";

const AddUserModal = ({ onClose, selectedRowVal = {}, setUserAdded }) => {
  const { role, clientCompany, clientId, makerLimit, approverLimit } =
    useAllProduct();
  const [userName, setUserName] = useState(selectedRowVal.userName || "");
  const [mailId, setMailId] = useState(selectedRowVal.mailId || "");
  const [phNumber, setPhNumber] = useState(selectedRowVal.phNumber || "");
  const [userRole, setUserRole] = useState(
    (selectedRowVal?.auth && selectedRowVal?.auth[0]?.role) || ""
  );
  const [makerLimitValue, setMakerLimitValue] = useState(
    selectedRowVal.makerLimit || ""
  );
  const [approverLimitValue, setApproverLimitValue] = useState(
    selectedRowVal.approverLimit || ""
  );
  const [errors, setErrors] = useState({});
  const isMakerLimitDisabled = [
    "Approver L1",
    "Approver L2",
    "Viewer",
  ].includes(userRole.toString());
  const isApproverLimitDisabled = ["Maker", "Viewer"].includes(
    userRole.toString()
  );

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Helper flags based on the selected role
    const nextRole = name === "role" ? value : userRole;
    const isMakerLimitDisabled = [
      "Approver L1",
      "Approver L2",
      "Viewer",
    ].includes(nextRole);
    const isApproverLimitDisabled = ["Maker", "Viewer"].includes(nextRole);

    // Update state
    switch (name) {
      case "role":
        setUserRole(value);

        // Clear maker limit if role disables it
        if (["Approver L1", "Approver L2", "Viewer"].includes(value)) {
          setMakerLimitValue("");
          setErrors((prev) => ({ ...prev, makerLimitValue: "" }));
        }

        // Clear approver limit if role disables it
        if (["Maker", "Viewer"].includes(value)) {
          setApproverLimitValue("");
          setErrors((prev) => ({ ...prev, approverLimitValue: "" }));
        }
        break;

      case "userName":
        setUserName(value);
        break;

      case "mailId":
        setMailId(value);
        break;

      case "phNumber":
        setPhNumber(value);
        break;

      case "makerLimitValue":
        if (!isMakerLimitDisabled) setMakerLimitValue(value);
        break;

      case "approverLimitValue":
        if (!isApproverLimitDisabled) setApproverLimitValue(value);
        break;

      default:
        break;
    }

    // Validate just the changed field
    let error = "";
    switch (name) {
      case "userName":
        if (!value.trim()) error = "Full Name is required.";
        break;

      case "mailId":
        if (!value.trim()) error = "Email ID is required.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          error = "Invalid email format.";
        break;

      case "phNumber":
        if (!value.trim()) error = "Phone number is required.";
        else if (!/^[6-9]\d{9}$/.test(value)) error = "Invalid phone number.";
        break;

      case "role":
        if (!value) error = "Role is required.";
        break;

      case "makerLimitValue":
        if (!isMakerLimitDisabled) {
          if (!value) error = "Maker limit is required.";
          else if (!/^\d+(\.\d{1,2})?$/.test(value))
            error = "Invalid maker limit.";
        }
        break;

      case "approverLimitValue":
        if (!isApproverLimitDisabled) {
          if (!value) error = "Approver limit is required.";
          else if (!/^\d+(\.\d{1,2})?$/.test(value))
            error = "Invalid approver limit.";
        }
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmitBankUser = async () => {
    if (!validateForm() || !validateLimits()) return;

    const userData = {
      userName,
      mailId,
      phNumber,
      userRole,
      makerLimitValue,
      approverLimitValue,
      clientId,
    };

    const isEditMode = Object.keys(selectedRowVal).length > 0;

    const url = isEditMode
      ? `${config.node_api_endpoint}/api/users/${selectedRowVal.id}`
      : `${config.node_api_endpoint}/api/add-user`;

    const method = isEditMode ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) throw new Error("Failed to submit");

      const result = await response.json();
      console.log(isEditMode ? "User updated:" : "User added:", result);

      setUserAdded(true);
      onClose();
    } catch (error) {
      console.error("Error submitting user data:", error);
      alert("Something went wrong. Please try again.");
    }
  };

  const handleSubmitClientUser = async () => {
    if (!validateForm() || !validateLimits()) return;

    const userData = {
      userName,
      mailId,
      phNumber,
      userRole,
      makerLimitValue,
      approverLimitValue,
      clientId,
      company_name: clientCompany,
    };

    console.log("userData-->", userData);

    const isEditMode = Object.keys(selectedRowVal).length > 0;

    const url = isEditMode
      ? `${config.node_api_endpoint}/api/company-users/${selectedRowVal.id}`
      : `${config.node_api_endpoint}/api/add-company-users`;

    const method = isEditMode ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) throw new Error("Failed to submit");

      const result = await response.json();
      console.log(
        isEditMode ? "Company user updated:" : "Company user added:",
        result
      );

      setUserAdded(true);
      onClose();
    } catch (error) {
      console.error("Error submitting company user data:", error);
      alert("Something went wrong. Please try again.");
    }
  };

  const validateLimits = () => {
    if (role.toString() === "Owner") return true;
    else {
      if (
        role.toString() !== "Owner" &&
        makerLimitValue &&
        Number(makerLimitValue) > Number(makerLimit)
      ) {
        alert(`Maker limit should not exceed ${makerLimit}`);
        return false;
      }

      if (
        approverLimitValue &&
        Number(approverLimitValue) > Number(approverLimit)
      ) {
        alert(`Approver limit should not exceed ${approverLimit}`);
        return false;
      }
      return true;
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!userName.trim()) newErrors.userName = "Full Name is required.";
    if (!mailId.trim()) newErrors.mailId = "Email ID is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mailId))
      newErrors.mailId = "Invalid email format.";

    if (!phNumber.trim()) newErrors.phNumber = "Phone number is required.";
    else if (!/^[6-9]\d{9}$/.test(phNumber))
      newErrors.phNumber = "Invalid phone number.";

    if (!userRole) newErrors.role = "Role is required.";

    if (!isMakerLimitDisabled) {
      if (makerLimitValue === "")
        newErrors.makerLimitValue = "Maker limit is required.";
      else if (!/^\d+(\.\d{1,2})?$/.test(makerLimitValue))
        newErrors.makerLimitValue = "Invalid maker limit.";
    }

    if (!isApproverLimitDisabled) {
      if (approverLimitValue === "")
        newErrors.approverLimitValue = "Approver limit is required.";
      else if (!/^\d+(\.\d{1,2})?$/.test(approverLimitValue))
        newErrors.approverLimitValue = "Invalid approver limit.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  console.log("company-->", userRole);
  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modalContainer}>
        <div className={styles.backButton}>
          <span className="px-1" onClick={onClose}>
            &#x2190;
          </span>
          {Object.keys(selectedRowVal)?.length > 0 ? "Edit" : "Add"} User
          Details
        </div>
        <TextField
          label="Full Name"
          variant="outlined"
          name="userName"
          fullWidth
          margin="normal"
          value={userName}
          onChange={handleChange}
          error={!!errors.userName}
          helperText={errors.userName}
        />
        <TextField
          label="Email Id"
          variant="outlined"
          name="mailId"
          fullWidth
          margin="normal"
          value={mailId}
          onChange={handleChange}
          error={!!errors.mailId}
          helperText={errors.mailId}
        />
        <TextField
          label="Phone number"
          variant="outlined"
          name="phNumber"
          fullWidth
          margin="normal"
          value={phNumber}
          onChange={handleChange}
          error={!!errors.phNumber}
          helperText={errors.phNumber}
        />
        <TextField
          select
          label="Role"
          name="role"
          value={userRole}
          onChange={handleChange}
          fullWidth
          sx={{
            "& .MuiInputBase-root": { borderRadius: "12px" },
            marginBottom: "5px",
            marginTop: "5px",
          }}
          error={!!errors.role}
          helperText={errors.role}
        >
          <MenuItem value="">Select Role</MenuItem>
          {role.toString() === "Owner" && (
            <MenuItem value="Super Admin">Super Admin</MenuItem>
          )}
          {(role.toString() === "Owner" ||
            role.toString() === "Super Admin") && (
            <MenuItem value="Admin">Admin</MenuItem>
          )}
          {(role.toString() === "Owner" ||
            role.toString() === "Super Admin" ||
            role.toString() === "Admin") && (
            <MenuItem value="Maker">Maker</MenuItem>
          )}
          <MenuItem value="Approver L1">Approver L1</MenuItem>
          <MenuItem value="Approver L2">Approver L2</MenuItem>
          <MenuItem value="Viewer">Viewer</MenuItem>
        </TextField>{" "}
        <TextField
          label="Maker limit"
          variant="outlined"
          name="makerLimitValue"
          fullWidth
          margin="normal"
          value={makerLimitValue}
          onChange={handleChange}
          error={!!errors.makerLimitValue}
          disabled={isMakerLimitDisabled}
          helperText={errors.makerLimitValue}
        />
        <TextField
          label="Approver Limit"
          name="approverLimitValue"
          variant="outlined"
          fullWidth
          margin="normal"
          value={approverLimitValue}
          onChange={handleChange}
          disabled={isApproverLimitDisabled}
          error={!!errors.approverLimitValue}
          helperText={errors.approverLimitValue}
        />
        <Button
          variant="contained"
          fullWidth
          className={styles.submitButton}
          onClick={() => {
            if (clientCompany && clientCompany !== "undefined") {
              handleSubmitClientUser();
            } else handleSubmitBankUser();
          }}
        >
          {Object.keys(selectedRowVal)?.length > 0 ? "Update" : "Submit"}
        </Button>
      </div>
    </div>
  );
};

export default AddUserModal;
